import { useSuspenseQuery } from '@apollo/client';
import { Box, Card, Skeleton, Typography } from '@mui/material';
import { type GraphQLError } from 'graphql';
import { Suspense, useState, type FC } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { Navigate, useParams } from 'react-router-dom';
import Logo from 'src/components/logo/LogoAmaru';
import Echarts, { EChartsOptionBuilder } from 'src/features/echarts';
import StandardListPagination from 'src/features/standardDesign/components/listView/StandardListPagination';
import GET_PUBLIC_ORGANIZATION, { PublicDevice } from '../queries/publicOrganization';

const CHART_HEIGHT = 240;

const ErrorFallback: FC<FallbackProps> = ({ error }) => {
  const isNotFoundError = error.graphQLErrors.some(
    (e: GraphQLError) => e.extensions?.code === 'NOT_FOUND',
  );
  if (isNotFoundError) return <Navigate to="/404" />;

  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre style={{ color: 'red' }}>{error.message}</pre>
    </div>
  );
};

const ListSkeleton = () => (
  <>
    <Box display="flex" my={2} justifyContent="space-between">
      <Box />
      <Skeleton variant="rounded" height={40} width={370} />
    </Box>
    {Array.from({ length: 5 }).map((_, index) => (
      <Skeleton key={index} variant="rounded" height={CHART_HEIGHT} sx={{ my: 2 }} />
    ))}
  </>
);

const PublicDevicesView = () => {
  const { name } = useParams();

  if (!name) return <Navigate to="/404" />;
  const organizationName = name.replace(/_/g, ' ');

  return (
    <Box maxWidth="lg" mx="auto" width="100%" px={2} pb={8}>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Box
          component="header"
          display="flex"
          gap={4}
          justifyContent="space-between"
          alignItems="center"
          p={2}
          mb={1}
        >
          <Logo style={{ maxHeight: 60, maxWidth: '40vw' }} />
          <Typography
            component="h1"
            style={{ fontSize: 'min(calc(2vw + 8px), 20px)' }}
            textAlign="right"
          >
            {organizationName}
          </Typography>
        </Box>
        <Suspense fallback={<ListSkeleton />}>
          <PublicCharts organizationName={organizationName} />
        </Suspense>
      </ErrorBoundary>
    </Box>
  );
};

interface Props {
  organizationName: string;
}

const PublicCharts: FC<Props> = ({ organizationName }) => {
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(1);
  const { data } = useSuspenseQuery(GET_PUBLIC_ORGANIZATION, {
    variables: {
      input: { name: organizationName },
      pagination: {
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage,
      },
    },
  });

  const devices = data.publicOrganization.devices.nodes;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <StandardListPagination
        totalItems={data.publicOrganization.devices.pageInfo.totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
      />
      {devices.map((device) => (
        <PublicChart device={device} key={device.id} />
      ))}
    </Box>
  );
};

const PublicChart = ({ device }: { device: PublicDevice }) => {
  const { flow, level } = device.telemetry;

  const option = new EChartsOptionBuilder({
    toolbox: {
      disabled: true,
    },
    override(option) {
      return {
        ...option,
        title: {
          text: device.name,
          show: true,
          textStyle: {
            color: 'white',
          },
        },
        legend: {
          ...option.legend,
          right: 0,
          left: 'auto',
        },
      };
    },
  });
  option
    .addXAxis({
      type: 'time',
      id: 'category',
    })
    .addYAxis(
      {
        id: 'flow',
        type: 'value',
        unit: flow.unit,
      },
      (axis) => {
        return {
          ...axis,
          splitNumber: 3,
        };
      },
    )
    .addYAxis(
      {
        id: 'level',
        type: 'value',
        unit: level.unit,
      },
      (axis) => {
        return {
          ...axis,
          splitNumber: 3,
        };
      },
    );

  option.addSeries([
    {
      meta: {
        name: 'Caudal',
        yAxisId: 'flow',
        xAxisId: 'category',
        type: 'area',
        dataType: 'flow',
        data: flow.data,
      },
      value(value) {
        return {
          ...value,
          smooth: true,
        };
      },
    },
    {
      meta: {
        name: 'Nivel',
        yAxisId: 'level',
        xAxisId: 'category',
        type: 'line',
        dataType: 'level',
        data: level.data,
      },
      value(value) {
        return {
          ...value,
          smooth: true,
        };
      },
    },
  ]);
  return (
    <Card
      sx={{
        px: 2,
        pt: 2,
        height: CHART_HEIGHT,
      }}
    >
      <Echarts option={option} />
    </Card>
  );
};

export default PublicDevicesView;
